import { styled } from "@pigment-css/react";
import { RadioGroup } from "radix-ui";
import { RecurrenceCadence } from "@acto/gql/graphql";
import { useTranslations } from "@acto/web-common/locales";
import { pressInteraction } from "@acto/taskpane/field-sales-2/styles/pressInteraction";

const Group = styled(RadioGroup.Root)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[8],
  width: "100%",
}));

const Option = styled(RadioGroup.Item)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: theme.fieldSalesTwo.spacing[8],
  height: theme.fieldSalesTwo.spacing[48],
  paddingInline: theme.fieldSalesTwo.spacing[20],
  boxSizing: "border-box",
  border: `${theme.strokeWidth.thin} solid ${theme.fieldSalesTwo.colors.neutral[300]}`,
  borderRadius: theme.fieldSalesTwo.radius[38],
  background: "transparent",
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size.base,
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[148],
  color: theme.fieldSalesTwo.colors.neutral[900],
  cursor: "pointer",
  WebkitTapHighlightColor: "transparent",
  transition: `background-color ${theme.fieldSalesTwo.motion.duration.fast} ${theme.fieldSalesTwo.motion.easing.standard}, transform ${theme.fieldSalesTwo.motion.duration.fast} ${theme.fieldSalesTwo.motion.easing.standard}`,
  ...pressInteraction(theme),
  // Mirrors the calendar's selected-day fill so the active cadence reads as the same monochrome selection.
  '&[data-state="checked"]': {
    background: theme.fieldSalesTwo.colors.neutral[900],
    borderColor: theme.fieldSalesTwo.colors.neutral[900],
    color: "#fff",
  },
}));

const Ring = styled("span")(({ theme }) => ({
  flexShrink: 0,
  width: 22,
  height: 22,
  boxSizing: "border-box",
  borderRadius: theme.fieldSalesTwo.radius.circular,
  border: `${theme.strokeWidth.regular} solid ${theme.fieldSalesTwo.colors.neutral[400]}`,
  '[data-state="checked"] > &': {
    background: "#fff",
    borderColor: "#fff",
  },
}));

const CADENCES = [
  RecurrenceCadence.Weekly,
  RecurrenceCadence.Biweekly,
  RecurrenceCadence.EveryFourWeeks,
  RecurrenceCadence.Monthly,
] as const;

const cadenceLabelKey: Record<RecurrenceCadence, string> = {
  [RecurrenceCadence.Weekly]: "cadenceWeekly",
  [RecurrenceCadence.Biweekly]: "cadenceBiweekly",
  [RecurrenceCadence.EveryFourWeeks]: "cadenceEveryFourWeeks",
  [RecurrenceCadence.Monthly]: "cadenceMonthly",
};

interface CadenceOptionsProps {
  value: RecurrenceCadence;
  onChange: (value: RecurrenceCadence) => void;
}

export function CadenceOptions({ value, onChange }: CadenceOptionsProps) {
  const t = useTranslations("fieldSalesTwo.planMeeting");

  return (
    <Group value={value} onValueChange={(next) => onChange(next as RecurrenceCadence)} aria-label={t("repeat")}>
      {CADENCES.map((cadence) => (
        <Option key={cadence} value={cadence}>
          {t(cadenceLabelKey[cadence])}
          <Ring aria-hidden="true" />
        </Option>
      ))}
    </Group>
  );
}
